import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Badge,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  CircularProgress,
} from '@mui/material';
import {
  Send as SendIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  FilterList,
  Clear,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  MoreVert as MoreVertIcon,
  ArrowBack as ArrowBackIcon,
  PersonAdd as PersonAddIcon,
  AutoAwesome as AutoAwesomeIcon,
  SwapHoriz as SwapHorizIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { format, parseISO, isToday, isYesterday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { ChatNotificationData } from '../services/signalRService';
import { Lead, User, Conversation, Message } from '../types';
import {
  fetchConversations,
  fetchConversationMessages,
  sendWhatsAppMessage,
  sendWhatsAppImage,
  fetchLeadByPhoneNumber,
  assignConversation,
  fetchSalesUsers,
  suggestResponse,
  fetchAgents,
  updateConversationStatus,
  updateConversationName,
  getAllActiveAgentUsers,
  BackendChatHistoryRS,
  BackendLead,
  BackendUser,
  BackendAgent,
  BackendChatMessage,
  ConversationStatus,
  AgentUserRS
} from '../utils/api';
import { agentApi } from '../utils/agentApi';
import { splitMessageContent, MessageMedia } from '../utils/messageUtils';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LeadDetailModal from '../components/LeadDetailModal';
import AssignConversationModal from '../components/AssignConversationModal';
import AgentSelectionModal from '../components/AgentSelectionModal';
import NotificationCenter from '../components/NotificationCenter';
import CreateLeadModal from '../components/CreateLeadModal';
import { isManagerRole, isAdminLike, isAdminRole } from '../utils/roleMapping';

interface ConversationsPageProps {
  onOpenLeadDetail?: (leadId: string) => void;
}

const ConversationsPage: React.FC<ConversationsPageProps> = ({ onOpenLeadDetail }) => {
  const { user, permissions } = useAuth();
  const { onConversationUpdate, offConversationUpdate, onChatNotification, offChatNotification } = useNotifications();
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isMobileHorizontal = useMediaQuery('(max-width: 1024px) and (orientation: landscape)');

  const [conversations, setConversations] = useState<BackendChatHistoryRS[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<BackendChatHistoryRS | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasMoreConversations, setHasMoreConversations] = useState(true);
  const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingImage, setSendingImage] = useState(false);
  const [suggestingResponse, setSuggestingResponse] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageCaption, setImageCaption] = useState('');

  // Filtros
  const [nameFilter, setNameFilter] = useState('');
  const [phoneFilter, setPhoneFilter] = useState('');
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'week' | 'month' | 'custom'>('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [customDateOpen, setCustomDateOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ConversationStatus | 'all'>('all');
  const [assignedToFilter, setAssignedToFilter] = useState<string>('all');
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Lead relacionado
  const [relatedLead, setRelatedLead] = useState<BackendLead | null>(null);
  const [leadLoading, setLeadLoading] = useState(false);

  // Mapa de conversas com seus Leads relacionados
  const [conversationLeads, setConversationLeads] = useState<Map<string, BackendLead>>(new Map());

  // Assignment modal
  const [assignModalOpen, setAssignModalOpen] = useState(false);

  // Users for assignment display
  const [users, setUsers] = useState<BackendUser[]>([]);
  const [agentUsers, setAgentUsers] = useState<AgentUserRS[]>([]);

  // Estados para edição do nome da conversa
  const [isEditingConversationName, setIsEditingConversationName] = useState(false);
  const [editedConversationName, setEditedConversationName] = useState('');
  const [currentConversationName, setCurrentConversationName] = useState('');

  // Sincroniza o nome da conversa quando selecionada
  useEffect(() => {
    if (selectedConversation) {
      setCurrentConversationName(selectedConversation.name || '');
    }
  }, [selectedConversation]);

  // Carrega Leads das conversas quando elas são carregadas
  useEffect(() => {
    if (conversations.length > 0) {
      conversations.forEach(conversation => {
        // Só carrega se ainda não foi carregado
        if (!conversationLeads.has(conversation.id)) {
          loadConversationLead(conversation);
        }
      });
    }
  }, [conversations]);

  // Agent selection
  const [selectedAgent, setSelectedAgent] = useState<BackendAgent | null>(null);
  const [agentSelectionOpen, setAgentSelectionOpen] = useState(false);
  const [availableAgents, setAvailableAgents] = useState<BackendAgent[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);

  // Create lead modal
  const [createLeadModalOpen, setCreateLeadModalOpen] = useState(false);

  // Delete conversation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingConversation, setDeletingConversation] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<BackendChatHistoryRS | null>(null);

  // Mobile header menu
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-refresh intervals para Admin e Manager
  const conversationsIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const messagesIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Controle de scroll automático
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const lastMessageCountRef = useRef<number>(0);
  const shouldAutoScrollRef = useRef<boolean>(true);

  // Ref para a lista de conversas (scroll infinito)
  const conversationsListRef = useRef<HTMLUListElement>(null);

  // Clear skipAgentModal flag if user navigates manually (no conversationId)
  useEffect(() => {
    const conversationIdFromUrl = searchParams.get('conversationId');
    if (!conversationIdFromUrl) {
      // If there's no conversationId, this is manual navigation, clear the flag
      const hadFlag = localStorage.getItem('skipAgentModal') === 'true';
      if (hadFlag) {
        localStorage.removeItem('skipAgentModal');
        console.log('🔗 ConversationsPage - Manual navigation detected, cleared skipAgentModal flag');
      }
    }
  }, [searchParams]);

  const scrollToBottom = (force: boolean = false) => {
    if (force || shouldAutoScrollRef.current) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest'
        });
      }, 100);
    }
  };

  // Detectar se o usuário está próximo do final da conversa
  const checkIfShouldAutoScroll = () => {
    if (!messagesContainerRef.current) return;

    const container = messagesContainerRef.current;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    const threshold = isMobile ? 150 : 100;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold;
    shouldAutoScrollRef.current = isNearBottom;
  };

  // Controlar scroll automático baseado no contexto
  useEffect(() => {
    const currentMessageCount = messages.length;
    const previousMessageCount = lastMessageCountRef.current;

    // Se é uma nova conversa (mudou de 0 para algum número), sempre fazer scroll
    if (previousMessageCount === 0 && currentMessageCount > 0) {
      scrollToBottom(true);
    }
    // Se há novas mensagens e o usuário está próximo do final, fazer scroll
    else if (currentMessageCount > previousMessageCount && shouldAutoScrollRef.current) {
      scrollToBottom(true);
    }
    // Se é apenas um refresh (mesmo número de mensagens), não fazer scroll automático

    lastMessageCountRef.current = currentMessageCount;
  }, [messages]);

  useEffect(() => {
    if (selectedAgent) {
      loadConversations();
    }
  }, [nameFilter, phoneFilter, dateFilter, customStartDate, customEndDate, statusFilter, assignedToFilter, selectedAgent]);

  // Load agents on component mount and handle auto-selection
  useEffect(() => {
    const loadAvailableAgents = async () => {
      setAgentsLoading(true);
      try {
        const agentsList = await fetchAgents();
        // Filter only enabled agents
        const enabledAgents = agentsList.filter(agent => agent.status === 'Enabled');
        setAvailableAgents(enabledAgents);

        // Check localStorage flag to see if we should skip the modal
        const skipAgentModal = localStorage.getItem('skipAgentModal') === 'true';
        const conversationIdFromUrl = window.location.search.includes('conversationId=');

        console.log('🤖 Agent loading - enabled agents count:', enabledAgents.length);
        console.log('🤖 Agent loading - selected agent:', selectedAgent?.name || 'none');
        console.log('🤖 Agent loading - has conversationId in URL:', conversationIdFromUrl);
        console.log('🤖 Agent loading - skipAgentModal flag:', skipAgentModal);

        if (enabledAgents.length === 1 && !selectedAgent) {
          // Auto-select the single available agent
          console.log('🤖 Auto-selecting single agent:', enabledAgents[0].name);
          setSelectedAgent(enabledAgents[0]);
        } else if (enabledAgents.length > 1 && !selectedAgent && !skipAgentModal) {
          // Only show selection modal if we're not skipping it
          console.log('🤖 Multiple agents available, showing selection modal');
          setAgentSelectionOpen(true);
        } else if (enabledAgents.length > 1 && !selectedAgent && skipAgentModal) {
          console.log('🤖 Multiple agents available but skipAgentModal flag is set, skipping modal');
        }
        // If no agents available, do nothing (will show no agents message)
        // If conversationIdFromUrl exists, let the URL parameter handler deal with agent selection
      } catch (error) {
        console.error('Erro ao carregar agentes:', error);
        toast.error('Erro ao carregar agentes');
      } finally {
        setAgentsLoading(false);
      }
    };

    loadAvailableAgents();
  }, [selectedAgent]); // Remove isFromNotification dependency

  // Close agent selection modal if we have skipAgentModal flag
  useEffect(() => {
    const skipAgentModal = localStorage.getItem('skipAgentModal') === 'true';
    if (skipAgentModal && agentSelectionOpen) {
      console.log('🤖 Closing agent modal due to skipAgentModal flag');
      setAgentSelectionOpen(false);
    }
  }, [agentSelectionOpen]);

  // Additional safety: close modal immediately if it opens when we have conversationId
  useEffect(() => {
    const conversationIdFromUrl = searchParams.get('conversationId');
    if (conversationIdFromUrl && agentSelectionOpen) {
      console.log('🤖 Force closing agent modal - notification detected');
      setTimeout(() => setAgentSelectionOpen(false), 0);
    }
  }, [agentSelectionOpen, searchParams]);

  // Handle agent selection modal close
  const handleAgentSelectionClose = () => {
    setAgentSelectionOpen(false);
    // If no agent is selected after closing, we should allow reopening
    // The modal will remain closeable but can be reopened
  };

  useEffect(() => {
    const loadUsers = async () => {
      if (canAssignConversation()) {
        try {
          const salesUsers = await fetchSalesUsers();
          setUsers(salesUsers);

          // Also load agent users for assignment
          const agentUsersData = await getAllActiveAgentUsers();
          setAgentUsers(agentUsersData);
        } catch (error) {
          console.error('Erro ao carregar usuários:', error);
        }
      }
    };

    loadUsers();
  }, [user?.role]);

  // Listen for real-time conversation updates from notifications
  useEffect(() => {
    const handleConversationUpdate = (conversationId: string, updateType: 'assigned' | 'transferred') => {
      console.log(`🚀 ConversationsPage - Conversation ${conversationId} was ${updateType}, refreshing conversations...`);

      // Refresh conversations data
      let isMounted = true;
      const refreshData = async () => {
        try {
          if (isMounted && selectedAgent) {
            await refreshConversations();
          }
        } catch (error) {
          console.error('Failed to refresh conversations:', error);
        }
      };

      refreshData();

      return () => {
        isMounted = false;
      };
    };

    onConversationUpdate(handleConversationUpdate);

    return () => {
      offConversationUpdate(handleConversationUpdate);
    };
  }, [onConversationUpdate, offConversationUpdate, selectedAgent]);

  // Listen for real-time chat notifications
  useEffect(() => {
    const handleChatNotification = (chatNotification: ChatNotificationData) => {
      console.log(`💬 ConversationsPage - Chat notification received:`, chatNotification);

      // Only refresh if the notification is for the currently selected agent
      if (selectedAgent && chatNotification.agentId === selectedAgent.id) {
        console.log(`💬 ConversationsPage - Refreshing conversations for agent ${selectedAgent.name}`);

        // Refresh conversations to show new messages (preserve already loaded)
        refreshConversations();

        // If the notification is for the currently selected conversation, refresh messages
        if (selectedConversation && chatNotification.chatId === selectedConversation.id) {
          console.log(`💬 ConversationsPage - Refreshing messages for current conversation`);

          // Refresh messages for the current conversation
          fetchConversationMessages(selectedConversation.id)
            .then(conversationMessages => {
              const mappedMessages: Message[] = conversationMessages.map((msg: BackendChatMessage, index: number) => ({
                _id: { $oid: `msg-${selectedConversation.id}-${index}` },
                role: msg.role as 'assistant' | 'user',
                content: msg.content,
                timestamp: { $date: msg.timestamp }
              }));
              setMessages(mappedMessages);
            })
            .catch(error => {
              console.error('Error refreshing conversation messages:', error);
            });
        }
      }
    };

    onChatNotification(handleChatNotification);

    return () => {
      offChatNotification(handleChatNotification);
    };
  }, [onChatNotification, offChatNotification, selectedAgent, selectedConversation]);

  // Auto-refresh das conversas para Admin e Manager
  useEffect(() => {
    if (!isAdminOrManager() || !selectedAgent) {
      // Limpar interval se não for Admin/Manager ou não tiver agente selecionado
      if (conversationsIntervalRef.current) {
        clearInterval(conversationsIntervalRef.current);
        conversationsIntervalRef.current = null;
      }
      return;
    }

    // Configurar auto-refresh das conversas a cada 10 segundos
    conversationsIntervalRef.current = setInterval(() => {
      refreshConversations();
    }, 10000);

    return () => {
      if (conversationsIntervalRef.current) {
        clearInterval(conversationsIntervalRef.current);
        conversationsIntervalRef.current = null;
      }
    };
  }, [selectedAgent, user?.role, nameFilter, phoneFilter, dateFilter, customStartDate, customEndDate, statusFilter, assignedToFilter]);

  // Auto-refresh das mensagens da conversa aberta para Admin e Manager
  useEffect(() => {
    if (!isAdminOrManager() || !selectedConversation) {
      // Limpar interval se não for Admin/Manager ou não tiver conversa selecionada
      if (messagesIntervalRef.current) {
        clearInterval(messagesIntervalRef.current);
        messagesIntervalRef.current = null;
      }
      return;
    }

    // Configurar auto-refresh das mensagens a cada 10 segundos
    messagesIntervalRef.current = setInterval(async () => {
      try {
        const conversationMessages = await fetchConversationMessages(selectedConversation.id);
        const mappedMessages: Message[] = conversationMessages.map((msg: BackendChatMessage, index: number) => ({
          _id: { $oid: `msg-${selectedConversation.id}-${index}` },
          role: msg.role as 'assistant' | 'user',
          content: msg.content,
          timestamp: { $date: msg.timestamp }
        }));
        setMessages(mappedMessages);
      } catch (error) {
        console.error('Erro ao atualizar mensagens automaticamente:', error);
      }
    }, 10000);

    return () => {
      if (messagesIntervalRef.current) {
        clearInterval(messagesIntervalRef.current);
        messagesIntervalRef.current = null;
      }
    };
  }, [selectedConversation, user?.role]);

  const PAGE_SIZE = 10;

  const loadConversations = async () => {
    if (!selectedAgent) return;

    setLoading(true);
    setConversations([]);
    setHasMoreConversations(true);

    try {
      const filters = {
        skip: 0,
        take: PAGE_SIZE,
        name: nameFilter || undefined,
        phone: phoneFilter || undefined,
        startDate: getStartDateISO(),
        endDate: getEndDateISO(),
        agentId: selectedAgent.id,
        assignedToUserId: assignedToFilter !== 'all' ? (assignedToFilter === '' ? 'unassigned' : assignedToFilter) : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        includeFinished: statusFilter === 'Finalizado' || statusFilter === 'all',
      };

      const data = await fetchConversations(filters);

      const uniqueConversations = (data || []).filter((conversation, index, self) =>
        index === self.findIndex((c) => c.id === conversation.id)
      );

      setConversations(uniqueConversations);
      setHasMoreConversations((data?.length || 0) === PAGE_SIZE);
    } catch (error) {
      console.error('Erro ao carregar conversas:', error);
      toast.error('Erro ao carregar conversas');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMoreConversations = async () => {
    if (!selectedAgent || loadingMoreConversations || !hasMoreConversations) return;

    setLoadingMoreConversations(true);
    try {
      const currentSkip = conversations.length;
      const filters = {
        skip: currentSkip,
        take: 50,
        name: nameFilter || undefined,
        phone: phoneFilter || undefined,
        startDate: getStartDateISO(),
        endDate: getEndDateISO(),
        agentId: selectedAgent.id,
        assignedToUserId: assignedToFilter !== 'all' ? (assignedToFilter === '' ? 'unassigned' : assignedToFilter) : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        includeFinished: statusFilter === 'Finalizado' || statusFilter === 'all',
      };

      const data = await fetchConversations(filters);

      // Filtrar duplicatas antes de adicionar
      setConversations(prev => {
        const existingIds = new Set(prev.map(c => c.id));
        const newConversations = (data || []).filter(c => !existingIds.has(c.id));
        return [...prev, ...newConversations];
      });

      setHasMoreConversations((data?.length || 0) === 50);
    } catch (error) {
      console.error('Erro ao carregar mais conversas:', error);
      toast.error('Erro ao carregar mais conversas');
    } finally {
      setLoadingMoreConversations(false);
    }
  };

  // Refresh inteligente que preserva conversas já carregadas
  const refreshConversations = async () => {
    if (!selectedAgent) return;

    try {
      const hasSearchFilters = !!(nameFilter || phoneFilter);

      const filters = {
        skip: 0,
        take: hasSearchFilters ? 10000 : 50,
        name: nameFilter || undefined,
        phone: phoneFilter || undefined,
        startDate: getStartDateISO(),
        endDate: getEndDateISO(),
        agentId: selectedAgent.id,
        assignedToUserId: assignedToFilter !== 'all' ? (assignedToFilter === '' ? 'unassigned' : assignedToFilter) : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        includeFinished: statusFilter === 'Finalizado' || statusFilter === 'all',
      };

      const freshData = await fetchConversations(filters);

      const uniqueFreshData = (freshData || []).filter((conversation, index, self) =>
        index === self.findIndex((c) => c.id === conversation.id)
      );

      if (hasSearchFilters) {
        setConversations(uniqueFreshData);
        setHasMoreConversations(false);
      } else {
        setConversations(prev => {
          if (prev.length === 0) {
            return uniqueFreshData;
          }

          if (!uniqueFreshData || uniqueFreshData.length === 0) {
            return prev;
          }

          // Criar mapa das conversas já carregadas para busca rápida
          const existingMap = new Map(prev.map(c => [c.id, c]));
          const freshMap = new Map(uniqueFreshData.map(c => [c.id, c]));

          // Encontrar novas conversas (não estão no mapa existente)
          const newConversations = uniqueFreshData.filter(c => !existingMap.has(c.id));

          // Atualizar conversas existentes com dados frescos
          const updatedExisting = prev.map(c => freshMap.get(c.id) || c);

          // Combinar: novas no início + existentes atualizadas
          // Garantir que não há duplicatas no resultado final
          const merged = [...newConversations, ...updatedExisting];
          return merged.filter((conversation, index, self) =>
            index === self.findIndex((c) => c.id === conversation.id)
          );
        });

        setHasMoreConversations((freshData?.length || 0) === 50);
      }
    } catch (error) {
      console.error('Erro ao atualizar conversas:', error);
    }
  };

  const getStartDateISO = () => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (dateFilter) {
      case 'today':
        return startOfToday.toISOString();
      case 'week':
        return new Date(startOfToday.getTime() - 7 * 86400000).toISOString();
      case 'month':
        return new Date(startOfToday.getTime() - 30 * 86400000).toISOString();
      case 'custom':
        return customStartDate ? new Date(customStartDate).toISOString() : undefined;
      default:
        return undefined;
    }
  };

  const getEndDateISO = () => {
    if (dateFilter === 'custom') {
      return customEndDate ? new Date(customEndDate).toISOString() : undefined;
    }
    return undefined;
  };

  // Função auxiliar para carregar lead relacionado
  const loadRelatedLead = async (conversation: BackendChatHistoryRS) => {
    if (!conversation.userId) return;

    setLeadLoading(true);
    try {
      // Limpar formatação do telefone para busca
      const cleanPhone = conversation.userId.replace(/\D/g, '');
      const result = await fetchLeadByPhoneNumber(cleanPhone);

      // Verificar se o resultado é um erro (quando a API retorna 404)
      if (result && typeof result === 'object' && 'success' in result && !result.success) {
        setRelatedLead(null);
      } else {
        setRelatedLead(result as BackendLead);
      }
    } catch (error) {
      setRelatedLead(null);
    } finally {
      setLeadLoading(false);
    }
  };

  const handleConversationSelect = async (conversation: BackendChatHistoryRS) => {
    setSelectedConversation(conversation);
    setRelatedLead(null); // Reset do lead anterior

    // Reset do controle de scroll para nova conversa
    lastMessageCountRef.current = 0;
    shouldAutoScrollRef.current = true;

    // Converter mensagens do backend para o formato do frontend
    const frontendMessages = (conversation.messages || []).map(msg => ({
      _id: { $oid: `${Date.now()}-${Math.random()}` },
      role: msg.role as 'assistant' | 'user',
      content: msg.content,
      timestamp: { $date: msg.timestamp },
    }));
    setMessages(frontendMessages);

    // Carregar lead relacionado
    await loadRelatedLead(conversation);
  };

  // Helper function to render text with bold formatting (*text*) and line breaks
  const renderTextWithBold = (text: string): React.ReactNode => {
    if (!text) return null;

    const parts: (string | React.ReactElement)[] = [];
    let key = 0;

    // Split by bold patterns and process each segment
    const segments: Array<{ type: 'text' | 'bold'; content: string }> = [];
    const regex = /\*([^*]+)\*/g;
    let lastIndex = 0;
    let match;

    // Extract all bold segments and text segments
    while ((match = regex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        segments.push({
          type: 'text',
          content: text.substring(lastIndex, match.index)
        });
      }
      // Add bold segment
      segments.push({
        type: 'bold',
        content: match[1]
      });
      lastIndex = regex.lastIndex;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      segments.push({
        type: 'text',
        content: text.substring(lastIndex)
      });
    }

    // Process each segment and handle line breaks
    segments.forEach((segment) => {
      if (segment.type === 'bold') {
        parts.push(<strong key={key++}>{segment.content}</strong>);
      } else {
        // Process line breaks in text segments
        const lines = segment.content.split('\n');
        lines.forEach((line, lineIndex) => {
          if (lineIndex > 0) {
            parts.push(<br key={`br-${key++}`} />);
          }
          if (line) {
            parts.push(line);
          }
        });
      }
    });

    return parts.length > 0 ? <>{parts}</> : text;
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedConversation || sending) return;

    setSending(true);
    try {
      // Format message with user name in bold
      const userName = user?.name || 'Usuário';
      const formattedMessage = `*${userName}:*\n${messageText}`;

      const newMessage: Message = {
        _id: { $oid: Date.now().toString() },
        role: 'assistant',
        content: formattedMessage,
        timestamp: { $date: new Date().toISOString() },
      };

      // Always send via WhatsApp with formatted message
      await sendWhatsAppMessage(selectedConversation.id, formattedMessage);

      setMessages(prev => [...prev, newMessage]);
      setMessageText('');

      // Garantir scroll para nova mensagem enviada
      setTimeout(() => scrollToBottom(true), isMobile ? 200 : 100);

      // Update conversation status locally if it was not already "Em atendimento"
      if (selectedConversation.status === 'NaoAtendido' || selectedConversation.status === 'Finalizado') {
        setSelectedConversation({
          ...selectedConversation,
          status: 'EmAtendimento'
        });

        // Also refresh the conversations list to show updated status
        await refreshConversations();
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      toast.error('Erro ao enviar mensagem via WhatsApp');
    } finally {
      setSending(false);
    }
  };

  const handleSuggestResponse = async () => {
    if (!selectedConversation || suggestingResponse || messages.length === 0) return;

    setSuggestingResponse(true);
    try {
      const response = await suggestResponse(selectedConversation.id);
      setMessageText(response.suggestedResponse);
      toast.success('Sugestão de resposta gerada com sucesso!');
    } catch (error) {
      console.error('Erro ao sugerir resposta:', error);
      toast.error('Erro ao gerar sugestão de resposta');
    } finally {
      setSuggestingResponse(false);
    }
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Verificar se é uma imagem
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione apenas arquivos de imagem');
      return;
    }

    // Verificar tamanho (máximo 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 10MB');
      return;
    }

    setSelectedImage(file);

    // Criar preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleImageSend = async () => {
    if (!selectedImage || !selectedConversation || sendingImage) return;

    setSendingImage(true);
    try {
      // Converter imagem para base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const imageBase64 = e.target?.result as string;

          // Format caption with user name in bold
          const userName = user?.name || 'Usuário';
          const formattedCaption = imageCaption
            ? `*${userName}:*\n${imageCaption}`
            : `*${userName}:*`;

          const response = await sendWhatsAppImage(selectedConversation.id, imageBase64, formattedCaption);
          toast.success('Imagem enviada via WhatsApp com sucesso!');

          // Adicionar mensagem à conversa com a URL da imagem
          const messageContent = formattedCaption
            ? `${formattedCaption}\n${response.imageUrl}`
            : `*${userName}:*\n${response.imageUrl}`;
          const newMessage: Message = {
            _id: { $oid: Date.now().toString() },
            role: 'assistant',
            content: messageContent,
            timestamp: { $date: new Date().toISOString() },
          };

          setMessages(prev => [...prev, newMessage]);

          // Limpar seleção
          setSelectedImage(null);
          setImagePreview(null);
          setImageCaption('');

          // Garantir scroll para nova mensagem enviada
          setTimeout(() => scrollToBottom(true), isMobile ? 200 : 100);

          // Update conversation status locally if it was not already "Em atendimento"
          if (selectedConversation.status === 'NaoAtendido' || selectedConversation.status === 'Finalizado') {
            setSelectedConversation({
              ...selectedConversation,
              status: 'EmAtendimento'
            });

            // Also refresh the conversations list to show updated status
            await loadConversations();
          }
        } catch (error) {
          console.error('Erro ao enviar imagem:', error);
          toast.error('Erro ao enviar imagem via WhatsApp');
        } finally {
          setSendingImage(false);
        }
      };
      reader.readAsDataURL(selectedImage);
    } catch (error) {
      console.error('Erro ao processar imagem:', error);
      toast.error('Erro ao processar imagem');
      setSendingImage(false);
    }
  };

  const handleCancelImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setImageCaption('');
  };

  const [leadDetailOpen, setLeadDetailOpen] = useState(false);

  const handleOpenLead = () => {
    if (relatedLead) {
      if (onOpenLeadDetail) {
        onOpenLeadDetail(relatedLead.id);
      } else {
        // Abrir modal do lead em vez de navegar
        setLeadDetailOpen(true);
      }
    }
  };

  const handleAssignConversation = async (userId: string) => {
    if (!selectedConversation) return;

    try {
      await assignConversation(selectedConversation.id, userId);
      toast.success('Conversa atribuída com sucesso');

      // Refresh conversations to show updated assignment
      await loadConversations();
    } catch (error) {
      console.error('Erro ao atribuir conversa:', error);
      throw error;
    }
  };

  const canAssignConversation = () => {
    return !!user && (isAdminLike(user.role || '') || isManagerRole(user.role || ''));
  };

  const isAdminOrManager = () => {
    return !!user && (isAdminLike(user.role || '') || isManagerRole(user.role || ''));
  };

  // Use roleMapping helpers instead of local role checks

  const handleDeleteConversation = async () => {
    if (!conversationToDelete) return;
    setDeletingConversation(true);
    try {
      await agentApi.clearChatHistory(conversationToDelete.agentId, conversationToDelete.userId);
      toast.success('Conversa excluída com sucesso!');
      setDeleteDialogOpen(false);
      setConversationToDelete(null);
      // Refresh list and clear selection
      await loadConversations();
      if (selectedConversation && selectedConversation.id === conversationToDelete.id) {
        setSelectedConversation(null);
        setMessages([]);
        setRelatedLead(null);
      }
    } catch (error) {
      console.error('Erro ao excluir conversa:', error);
      toast.error('Erro ao excluir conversa');
    } finally {
      setDeletingConversation(false);
    }
  };

  const handleOpenConversation = async (conversationId: string) => {
    console.log('🎯 ConversationsPage - Opening conversation from notification:', conversationId);
    console.log('🎯 ConversationsPage - ConversationId type:', typeof conversationId);
    console.log('🎯 ConversationsPage - Selected agent:', selectedAgent);
    console.log('🎯 ConversationsPage - Available agents:', availableAgents.length);
    console.log('🎯 ConversationsPage - Current conversations:', conversations.length);

    // Ensure conversationId is a string
    const conversationIdStr = String(conversationId);

    try {
      // First, try to find the conversation by fetching it directly
      let targetConversation: BackendChatHistoryRS | null = null;

      // If we have an agent selected, look in current conversations first
      if (selectedAgent) {
        targetConversation = conversations.find(c => c.id === conversationIdStr) || null;
      }

      // If not found and we have an agent, refresh conversations
      if (!targetConversation && selectedAgent) {
        console.log('Conversation not found in current list, refreshing...');
        await loadConversations();
        targetConversation = conversations.find(c => c.id === conversationIdStr) || null;
      }

      // If still not found or no agent selected, we need to find the right agent
      if (!targetConversation) {
        console.log('Need to find the agent for this conversation...');

        // Try to fetch all conversations from all available agents to find the right one
        console.log('🔍 Searching through agents for conversation...');
        for (const agent of availableAgents) {
          try {
            console.log(`🔍 Checking agent: ${agent.name} (${agent.id})`);
            const agentConversations = await fetchConversations({ agentId: agent.id });
            console.log(`🔍 Found ${agentConversations.length} conversations for agent ${agent.name}`);
            const foundConversation = agentConversations.find(c => c.id === conversationIdStr);

            if (foundConversation) {
              console.log(`✅ Found conversation in agent ${agent.name}, selecting agent...`);

              // Select the agent first
              setSelectedAgent(agent);

              // Wait a bit for the state to update and conversations to load
              setTimeout(async () => {
                try {
                  // Now select the conversation
                  setSelectedConversation(foundConversation);

                  // Load messages for the conversation
                  const conversationMessages = await fetchConversationMessages(conversationIdStr);
                  const mappedMessages: Message[] = conversationMessages.map((msg: BackendChatMessage, index: number) => ({
                    _id: { $oid: `msg-${conversationIdStr}-${index}` },
                    role: msg.role as 'assistant' | 'user',
                    content: msg.content,
                    timestamp: { $date: msg.timestamp }
                  }));
                  setMessages(mappedMessages);

                  await loadRelatedLead(foundConversation);

                  toast.success(`Conversa aberta (Agente: ${agent.name})`);
                } catch (error) {
                  console.error('Error loading conversation messages:', error);
                  toast.error('Erro ao carregar mensagens da conversa');
                }
              }, 500);

              return; // Exit the function as we found and are handling the conversation
            }
          } catch (error) {
            console.error(`Error checking agent ${agent.name}:`, error);
          }
        }

        // If we get here, conversation wasn't found in any agent
        console.log('Conversation not found in any available agent');
        toast.error('Conversa não encontrada em nenhum agente disponível');
        return;
      }

      // If we have the conversation and agent is selected, proceed normally
      if (targetConversation && selectedAgent) {
        console.log('Found conversation, selecting it:', targetConversation);
        setSelectedConversation(targetConversation);

        // Load messages for the conversation
        const conversationMessages = await fetchConversationMessages(conversationIdStr);
        const mappedMessages: Message[] = conversationMessages.map((msg: BackendChatMessage, index: number) => ({
          _id: { $oid: `msg-${conversationIdStr}-${index}` },
          role: msg.role as 'assistant' | 'user',
          content: msg.content,
          timestamp: { $date: msg.timestamp }
        }));
        setMessages(mappedMessages);

        await loadRelatedLead(targetConversation);

        toast.success('Conversa aberta');
      }

    } catch (error) {
      console.error('Error opening conversation from notification:', error);
      toast.error('Erro ao abrir conversa');
    }
  };

  // Handle conversation ID from URL parameters (when redirected from notifications)
  useEffect(() => {
    const conversationIdFromUrl = searchParams.get('conversationId');
    if (conversationIdFromUrl && availableAgents.length > 0) {
      console.log('🔗 ConversationsPage - Found conversationId in URL:', conversationIdFromUrl);
      console.log('🔗 ConversationsPage - Coming from notification, skipping agent modal');

      // Clear the localStorage flag since we're processing the notification
      localStorage.removeItem('skipAgentModal');
      console.log('🔗 ConversationsPage - Cleared skipAgentModal flag from localStorage');

      // Clear the URL parameter to avoid reopening the same conversation
      setSearchParams(params => {
        params.delete('conversationId');
        return params;
      });

      // Open the conversation (this will automatically find and select the correct agent)
      handleOpenConversation(conversationIdFromUrl);
    }
  }, [searchParams, availableAgents.length, setSearchParams, handleOpenConversation]);

  const getAssignedUserName = (assignedToUserId?: string | null): string => {
    if (!assignedToUserId) return 'Não atribuída';

    // First try to find in traditional users
    const assignedUser = users.find(u => u.id === assignedToUserId);
    if (assignedUser) {
      return assignedUser.name;
    }

    // If not found, try to find in agent users
    const assignedAgentUser = agentUsers.find(au => au.userId === assignedToUserId);
    if (assignedAgentUser) {
      return assignedAgentUser.userName || assignedAgentUser.userId;
    }

    return 'Usuário não encontrado';
  };

  const handleAgentSelect = (agent: BackendAgent) => {
    setSelectedAgent(agent);
    setAgentSelectionOpen(false);
    // Clear current conversation selection when changing agents
    setSelectedConversation(null);
    setMessages([]);
    setRelatedLead(null);
  };


  const handleChangeAgent = () => {
    setSelectedAgent(null);
    // The useEffect will handle showing the modal or auto-selecting if only one agent
  };

  const handleCreateLeadFromConversation = () => {
    if (!selectedConversation) return;
    setCreateLeadModalOpen(true);
  };

  // Funções para edição do nome da conversa
  const handleStartEditingConversationName = () => {
    if (!selectedConversation) return;
    setEditedConversationName(currentConversationName);
    setIsEditingConversationName(true);
  };

  const handleCancelEditingConversationName = () => {
    setIsEditingConversationName(false);
    setEditedConversationName('');
  };

  const handleSaveConversationName = async () => {
    if (!selectedConversation || editedConversationName.trim() === '') return;

    try {
      await updateConversationName(selectedConversation.id, editedConversationName.trim());

      // Atualiza o estado local imediatamente
      setCurrentConversationName(editedConversationName.trim());

      // Atualiza a conversa na lista
      setConversations(prev =>
        prev.map(conv =>
          conv.id === selectedConversation.id
            ? { ...conv, name: editedConversationName.trim() }
            : conv
        )
      );

      // Atualiza a conversa selecionada
      setSelectedConversation(prev =>
        prev ? { ...prev, name: editedConversationName.trim() } : null
      );

      setIsEditingConversationName(false);
      setEditedConversationName('');

      toast.success('Nome da conversa atualizado!');
    } catch (error) {
      console.error('Erro ao atualizar nome da conversa:', error);
      toast.error('Erro ao atualizar nome da conversa');
      setEditedConversationName(currentConversationName);
      setIsEditingConversationName(false);
    }
  };

  const handleConversationNameKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSaveConversationName();
    } else if (event.key === 'Escape') {
      handleCancelEditingConversationName();
    }
  };

  // Mobile menu handlers
  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  // Função para verificar se uma conversa tem Lead relacionado
  const getConversationLead = (conversation: BackendChatHistoryRS): BackendLead | null => {
    return conversationLeads.get(conversation.id) || null;
  };

  // Função para carregar Lead de uma conversa específica
  const loadConversationLead = async (conversation: BackendChatHistoryRS) => {
    if (!conversation.userId) return;

    try {
      const cleanPhone = conversation.userId.replace(/\D/g, '');
      const lead = await fetchLeadByPhoneNumber(cleanPhone);

      if (lead && typeof lead === 'object' && 'success' in lead && !lead.success) {
        // Lead não encontrado
        setConversationLeads(prev => {
          const newMap = new Map(prev);
          newMap.delete(conversation.id);
          return newMap;
        });
      } else {
        // Lead encontrado
        setConversationLeads(prev => {
          const newMap = new Map(prev);
          newMap.set(conversation.id, lead);
          return newMap;
        });
      }
    } catch (error) {
      console.log('Erro ao carregar Lead da conversa:', error);
    }
  };

  const handleLeadCreated = async (newLead: any) => {
    // After creating the lead, refresh the related lead for the current conversation
    if (selectedConversation?.userId) {
      try {
        const cleanPhone = selectedConversation.userId.replace(/\D/g, '');
        const lead = await fetchLeadByPhoneNumber(cleanPhone);
        setRelatedLead(lead);

        // Atualiza o mapa de conversas
        setConversationLeads(prev => {
          const newMap = new Map(prev);
          newMap.set(selectedConversation.id, lead);
          return newMap;
        });

        toast.success('Lead criado e associado à conversa com sucesso!');
      } catch (error) {
        console.log('Lead criado mas não foi possível associar à conversa');
      }
    }
    setCreateLeadModalOpen(false);
  };

  const handleReopenConversation = async (conversationId: string) => {
    try {
      await updateConversationStatus(conversationId, 'EmAtendimento');
      toast.success('Conversa reaberta com sucesso!');

      // Refresh conversations to show updated status
      await loadConversations();

      // Update selected conversation status if it's the current one
      if (selectedConversation && selectedConversation.id === conversationId) {
        setSelectedConversation({
          ...selectedConversation,
          status: 'EmAtendimento'
        });
      }
    } catch (error) {
      console.error('Erro ao reabrir conversa:', error);
      toast.error('Erro ao reabrir conversa');
    }
  };

  const handleFinishConversation = async (conversationId: string) => {
    try {
      await updateConversationStatus(conversationId, 'Finalizado');
      toast.success('Conversa finalizada com sucesso!');

      // Refresh conversations to show updated status
      await loadConversations();

      // Update selected conversation status if it's the current one
      if (selectedConversation && selectedConversation.id === conversationId) {
        setSelectedConversation({
          ...selectedConversation,
          status: 'Finalizado'
        });
      }
    } catch (error) {
      console.error('Erro ao finalizar conversa:', error);
      toast.error('Erro ao finalizar conversa');
    }
  };

  const formatMessageTime = (timestamp: string) => {
    const date = parseISO(timestamp);
    if (isToday(date)) {
      return format(date, 'HH:mm');
    } else if (isYesterday(date)) {
      return 'Ontem';
    } else {
      return format(date, 'dd/MM/yyyy');
    }
  };

  const getConversationPreview = (conversation: BackendChatHistoryRS) => {
    const lastMessage = conversation.messages?.slice(-1)[0];
    return lastMessage?.content || 'Conversa iniciada';
  };

  const getConversationTime = (conversation: BackendChatHistoryRS) => {
    const lastMessage = conversation.messages?.slice(-1)[0];
    return lastMessage ? formatMessageTime(lastMessage.timestamp) : '';
  };

  const getStatusInfo = (status: ConversationStatus) => {
    switch (status) {
      case 'NaoAtendido':
        return {
          label: 'Não atendido',
          color: '#ef4444', // red
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(239,68,68,0.15)' : '#fef2f2'
        };
      case 'EmAtendimento':
        return {
          label: 'Em atendimento',
          color: '#f59e0b', // amber
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(245,158,11,0.15)' : '#fffbeb'
        };
      case 'Finalizado':
        return {
          label: 'Finalizado',
          color: '#10b981', // green
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.15)' : '#f0fdf4'
        };
      default:
        return {
          label: 'Desconhecido',
          color: '#6b7280', // gray
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(107,114,128,0.15)' : '#f9fafb'
        };
    }
  };

  const filteredConversations = conversations.filter(conv => {
    const matchName = !nameFilter || conv.name?.toLowerCase().includes(nameFilter.toLowerCase());
    const matchPhone = !phoneFilter || conv.userId?.includes(phoneFilter);
    return matchName && matchPhone;
  })

    .filter((conversation, index, self) =>
      index === self.findIndex((c) => c.id === conversation.id)
    );

  return (
    <Box sx={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: theme.palette.background.default,
      overflow: 'hidden',
    }}>
      {/* Header com botão voltar */}
      <Paper
        elevation={1}
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 0,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <IconButton
          onClick={() => navigate('/dashboard')}
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f1f5f9',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#313131' : '#e2e8f0',
            },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant={isMobile && !isMobileHorizontal ? "h6" : "h5"}
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              fontSize: isMobile && !isMobileHorizontal ? '1.1rem' : '1.5rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Conversas
          </Typography>
          {selectedAgent && (
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                mt: 0.5,
                fontSize: isMobile && !isMobileHorizontal ? '0.75rem' : '0.875rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              Agente: {selectedAgent.name}
            </Typography>
          )}
        </Box>
        {selectedAgent && availableAgents.length > 1 && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleChangeAgent}
            startIcon={<SwapHorizIcon />}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              borderColor: '#ff9800',
              color: '#ff9800',
              fontSize: isMobile && !isMobileHorizontal ? '0.75rem' : '0.875rem',
              px: isMobile && !isMobileHorizontal ? 1 : 2,
              py: isMobile && !isMobileHorizontal ? 0.5 : 1,
              '&:hover': {
                borderColor: '#f57c00',
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,152,0,0.12)' : '#fff3e0',
              },
            }}
          >
            {isMobile && !isMobileHorizontal ? 'Trocar' : 'Trocar Agente'}
          </Button>
        )}
        <NotificationCenter
          onOpenLeadDetail={onOpenLeadDetail}
          onOpenConversation={handleOpenConversation}
        />
      </Paper>

      {/* Conteúdo principal */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        overflow: 'hidden',
      }}>
        {/* Sidebar com lista de conversas */}
        {(!isMobile || !selectedConversation) && (
          <Paper
            elevation={0}
            sx={{
              width: isMobile ? '100%' : '380px',
              maxWidth: isMobile ? '100%' : '380px',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: theme.palette.background.paper,
              borderRight: `1px solid ${theme.palette.divider}`,
              borderRadius: 0,
            }}
          >
            {/* Header dos filtros */}
            <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Box sx={{ p: 3, pb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                    {filteredConversations.length} Conversas
                  </Typography>
                  {isAdminOrManager() && (
                    <Tooltip title="Atualizar conversas">
                      <IconButton
                        onClick={() => loadConversations()}
                        disabled={loading}
                        size="small"
                        sx={{
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(14,165,233,0.15)' : '#e0f2fe',
                          color: theme.palette.info.main,
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(14,165,233,0.25)' : '#b3e5fc',
                          },
                          '&:disabled': {
                            backgroundColor: theme.palette.action.disabledBackground,
                            color: theme.palette.action.disabled,
                          },
                        }}
                      >
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                {/* Filtros Header - Always visible */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    p: 1,
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                    },
                  }}
                  onClick={() => setFiltersExpanded(!filtersExpanded)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilterList sx={{ color: theme.palette.text.secondary, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                      Filtros
                    </Typography>
                  </Box>
                  <IconButton size="small" sx={{ color: theme.palette.text.secondary }}>
                    {filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
              </Box>

              {/* Collapsible Filters */}
              <Collapse in={filtersExpanded}>
                <Box sx={{ px: 3, pb: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      size="small"
                      placeholder="Buscar por nome..."
                      value={nameFilter}
                      onChange={(e) => setNameFilter(e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: theme.palette.mode === 'dark' ? '#313131' : '#f8fafc',
                          borderRadius: '10px',
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f1f5f9',
                          },
                          '&.Mui-focused': {
                            backgroundColor: theme.palette.background.paper,
                          },
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      size="small"
                      placeholder="Buscar por telefone..."
                      value={phoneFilter}
                      onChange={(e) => setPhoneFilter(e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: theme.palette.mode === 'dark' ? '#313131' : '#f8fafc',
                          borderRadius: '10px',
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f1f5f9',
                          },
                          '&.Mui-focused': {
                            backgroundColor: theme.palette.background.paper,
                          },
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <FormControl
                        size="small"
                        sx={{
                          flex: 1,
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.palette.mode === 'dark' ? '#313131' : '#f8fafc',
                            borderRadius: '10px',
                          },
                        }}
                      >
                        <InputLabel>Período</InputLabel>
                        <Select
                          value={dateFilter}
                          label="Período"
                          onChange={(e) => {
                            const value = e.target.value as typeof dateFilter;
                            setDateFilter(value);
                            if (value === 'custom') setCustomDateOpen(true);
                          }}
                        >
                          <MenuItem value="all">Todas</MenuItem>
                          <MenuItem value="today">Hoje</MenuItem>
                          <MenuItem value="week">Última semana</MenuItem>
                          <MenuItem value="month">Último mês</MenuItem>
                          <MenuItem value="custom">Personalizado</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    <FormControl
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: theme.palette.mode === 'dark' ? '#313131' : '#f8fafc',
                          borderRadius: '10px',
                        },
                      }}
                    >
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={statusFilter}
                        label="Status"
                        onChange={(e) => setStatusFilter(e.target.value as ConversationStatus | 'all')}
                      >
                        <MenuItem value="all">Todos</MenuItem>
                        <MenuItem value="NaoAtendido">Não atendido</MenuItem>
                        <MenuItem value="EmAtendimento">Em atendimento</MenuItem>
                        <MenuItem value="Finalizado">Finalizado</MenuItem>
                      </Select>
                    </FormControl>

                    {user && isAdminRole(user.role || '') && (
                      <FormControl
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.palette.mode === 'dark' ? '#313131' : '#f8fafc',
                            borderRadius: '10px',
                          },
                        }}
                      >
                        <InputLabel>Atendente</InputLabel>
                        <Select
                          value={assignedToFilter}
                          label="Atendente"
                          onChange={(e) => setAssignedToFilter(e.target.value)}
                        >
                          <MenuItem value="all">Todos</MenuItem>
                          <MenuItem value="">Não atribuídas</MenuItem>
                          {users.map((u) => (
                            <MenuItem key={u.id} value={u.id}>
                              {u.name}
                            </MenuItem>
                          ))}
                          {agentUsers.map((au) => (
                            <MenuItem key={au.userId} value={au.userId}>
                              {au.userName || au.userId}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setNameFilter('');
                          setPhoneFilter('');
                          setDateFilter('all');
                          setCustomStartDate('');
                          setCustomEndDate('');
                          setStatusFilter('all');
                          setAssignedToFilter('all');
                        }}
                        sx={{
                          backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f1f5f9',
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' ? '#313131' : '#e2e8f0',
                          },
                        }}
                        title="Limpar filtros"
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </Collapse>
            </Box>

            {/* Lista de conversas */}
            <List
              ref={conversationsListRef}
              sx={{ flex: 1, overflow: 'auto', p: 0 }}
              onScroll={(e) => {
                if (!hasMoreConversations || loadingMoreConversations || !selectedAgent) return;
                const target = e.currentTarget as HTMLElement;
                const threshold = 120; // px before bottom
                const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;
                if (nearBottom) {
                  handleLoadMoreConversations();
                }
              }}
            >
              {filteredConversations.map((conversation) => (
                <React.Fragment key={conversation.id}>
                  <ListItem
                    component="div"
                    onClick={() => handleConversationSelect(conversation)}
                    sx={{
                      cursor: 'pointer',
                      py: 2,
                      px: 3,
                      backgroundColor: selectedConversation?.id === conversation.id ? (theme.palette.mode === 'dark' ? 'rgba(14,165,233,0.12)' : '#f0f9ff') : 'transparent',
                      borderLeft: selectedConversation?.id === conversation.id ? `4px solid ${theme.palette.info.main}` : '4px solid transparent',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: selectedConversation?.id === conversation.id ? (theme.palette.mode === 'dark' ? 'rgba(14,165,233,0.2)' : '#e0f2fe') : (theme.palette.mode === 'dark' ? '#1a1a1a' : '#f8fafc'),
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          backgroundColor: selectedConversation?.id === conversation.id ? theme.palette.info.main : (theme.palette.mode === 'dark' ? '#313131' : '#64748b'),
                          width: 45,
                          height: 45,
                        }}
                      >
                        <PersonIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          {isEditingConversationName && selectedConversation?.id === conversation.id ? (
                            <TextField
                              value={editedConversationName}
                              onChange={(e) => setEditedConversationName(e.target.value)}
                              onKeyDown={handleConversationNameKeyPress}
                              onBlur={handleSaveConversationName}
                              autoFocus
                              variant="standard"
                              size="small"
                              sx={{
                                '& .MuiInput-underline:before': {
                                  borderBottomColor: theme.palette.primary.main,
                                },
                                '& .MuiInput-underline:after': {
                                  borderBottomColor: theme.palette.primary.main,
                                },
                                '& .MuiInputBase-input': {
                                  fontSize: '1rem',
                                  fontWeight: 600,
                                  padding: 0,
                                },
                              }}
                            />
                          ) : (
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                cursor: getConversationLead(conversation) ? 'default' : 'pointer',
                                '&:hover': {
                                  opacity: getConversationLead(conversation) ? 1 : 0.8,
                                }
                              }}
                              onClick={getConversationLead(conversation) ? undefined : handleStartEditingConversationName}
                            >
                              {getConversationLead(conversation)?.name || conversation.name || 'Usuário Anônimo'}
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>
                            {getConversationTime(conversation)}
                          </Typography>

                        </Box>
                      }
                      secondary={
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.text.secondary,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1,
                              }}
                            >
                              {getConversationPreview(conversation)}
                            </Typography>
                            {user && isAdminLike(user.role || '') && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConversationToDelete(conversation);
                                  setDeleteDialogOpen(true);
                                }}
                                sx={{
                                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(239,68,68,0.15)' : '#fee2e2',
                                  color: '#ef4444',
                                  '&:hover': {
                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(239,68,68,0.25)' : '#fecaca',
                                  },
                                }}
                                title="Limpar conversa"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem', opacity: 0.8 }}>
                              {conversation.userId}
                            </Typography>

                            {/* Status Chip */}
                            <Chip
                              size="small"
                              label={getStatusInfo(conversation.status).label}
                              sx={{
                                fontSize: '0.7rem',
                                height: 18,
                                background: getStatusInfo(conversation.status).backgroundColor,
                                color: getStatusInfo(conversation.status).color,
                                border: `1px solid ${getStatusInfo(conversation.status).color}40`,
                                fontWeight: 500,
                              }}
                            />

                            {canAssignConversation() && (
                              <Chip
                                size="small"
                                label={conversation.assignedToUserId ? getAssignedUserName(conversation.assignedToUserId) : 'Não atribuída'}
                                sx={{
                                  fontSize: '0.65rem',
                                  height: 16,
                                  background: conversation.assignedToUserId
                                    ? (theme.palette.mode === 'dark' ? 'rgba(34,197,94,0.15)' : '#dcfdf4')
                                    : (theme.palette.mode === 'dark' ? 'rgba(156,163,175,0.15)' : '#f3f4f6'),
                                  color: conversation.assignedToUserId ? theme.palette.success.dark : theme.palette.text.secondary,
                                  border: `1px solid ${conversation.assignedToUserId ? theme.palette.success.dark + '20' : theme.palette.text.secondary + '20'}`,
                                  maxWidth: '120px',
                                }}
                                title={conversation.assignedToUserId ? `Atribuída para: ${getAssignedUserName(conversation.assignedToUserId)}` : 'Conversa não atribuída'}
                              />
                            )}

                            {/* {!canAssignConversation() && conversation.assignedToUserId && (
                              <Chip
                                size="small"
                                label="Atribuída"
                                sx={{
                                  fontSize: '0.65rem',
                                  height: 16,
                                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(34,197,94,0.15)' : '#dcfdf4',
                                  border: `1px solid ${theme.palette.success.dark}20`,
                                }}
                              />
                            )} */}
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                  <Divider sx={{ mx: 3 }} />
                </React.Fragment>
              ))}

              {loadingMoreConversations && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {!hasMoreConversations && filteredConversations.length > 0 && !loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    Sem mais conversas
                  </Typography>
                </Box>
              )}

              {filteredConversations.length === 0 && !loading && (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <PersonIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
                    {!selectedAgent ? 'Nenhum agente selecionado' : 'Nenhuma conversa encontrada'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8', mb: !selectedAgent ? 2 : 0 }}>
                    {!selectedAgent
                      ? agentsLoading
                        ? 'Carregando agentes...'
                        : availableAgents.length === 0
                          ? 'Nenhum agente habilitado encontrado'
                          : 'Selecione um agente para visualizar suas conversas'
                      : 'Tente ajustar os filtros para encontrar conversas'
                    }
                  </Typography>
                  {!selectedAgent && availableAgents.length > 1 && !agentsLoading && (
                    <Button
                      variant="contained"
                      onClick={() => setAgentSelectionOpen(true)}
                      sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        px: 3,
                        backgroundColor: theme.palette.info.main,
                        color: theme.palette.mode === 'dark' ? '#000' : undefined,
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark' ? '#0284c7' : '#0284c7',
                        },
                      }}
                    >
                      Selecionar Agente
                    </Button>
                  )}
                </Box>
              )}
            </List>
          </Paper>
        )}

        {/* Área principal do chat - versão desktop e mobile */}
        {(!isMobile || selectedConversation) && (
          <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden'
          }}>
            {selectedConversation ? (
              <>
                {/* Header do chat */}
                <Paper
                  elevation={0}
                  sx={{
                    p: isMobileHorizontal ? 1.5 : (isMobile ? 1.5 : 2.5),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: theme.palette.background.paper,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    flexShrink: 0,
                    overflow: 'hidden',
                    minHeight: isMobileHorizontal ? '60px' : 'auto',
                  }}
                >
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobileHorizontal ? 1.5 : 2,
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                  }}>
                    {(isMobile || isMobileHorizontal) && (
                      <IconButton
                        onClick={() => setSelectedConversation(null)}
                        size="small"
                        sx={{
                          backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f1f5f9',
                          width: isMobileHorizontal ? 32 : 36,
                          height: isMobileHorizontal ? 32 : 36,
                          flexShrink: 0,
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' ? '#313131' : '#e2e8f0',
                          },
                        }}
                      >
                        <ArrowBackIcon fontSize={isMobileHorizontal ? "small" : "medium"} />
                      </IconButton>
                    )}
                    <Avatar sx={{
                      backgroundColor: theme.palette.info.main,
                      width: isMobileHorizontal ? 40 : 50,
                      height: isMobileHorizontal ? 40 : 50,
                      flexShrink: 0,
                    }}>
                      <PersonIcon fontSize={isMobileHorizontal ? "small" : "medium"} />
                    </Avatar>
                    <Box sx={{
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                    }}>
                      <Typography
                        variant={isMobileHorizontal ? "subtitle1" : "h6"}
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                          fontSize: isMobileHorizontal ? '0.9rem' : (isMobile ? '0.9rem' : '1.25rem'),
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {selectedConversation ? (getConversationLead(selectedConversation)?.name || selectedConversation.name || 'Usuário Anônimo') : 'Usuário Anônimo'}
                      </Typography>
                      {/* Mobile vertical: Stack layout */}
                      {isMobile && !isMobileHorizontal ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.secondary,
                              fontSize: '0.75rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {selectedConversation.userId || 'Telefone não identificado'}
                          </Typography>
                          <Chip
                            size="small"
                            label={getStatusInfo(selectedConversation.status).label}
                            sx={{
                              fontSize: '0.65rem',
                              height: 16,
                              background: getStatusInfo(selectedConversation.status).backgroundColor,
                              color: getStatusInfo(selectedConversation.status).color,
                              border: `1px solid ${getStatusInfo(selectedConversation.status).color}40`,
                              fontWeight: 500,
                              alignSelf: 'flex-start',
                            }}
                          />
                        </Box>
                      ) : (
                        /* Desktop and mobile horizontal: Side by side layout */
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: isMobileHorizontal ? 0.5 : 1,
                          flexWrap: 'nowrap',
                          overflow: 'hidden',
                        }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.secondary,
                              fontSize: isMobileHorizontal ? '0.7rem' : '0.875rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              minWidth: 0,
                            }}
                          >
                            {selectedConversation.userId || 'Telefone não identificado'}
                          </Typography>
                          <Chip
                            size="small"
                            label={getStatusInfo(selectedConversation.status).label}
                            sx={{
                              fontSize: '0.7rem',
                              height: 18,
                              background: getStatusInfo(selectedConversation.status).backgroundColor,
                              color: getStatusInfo(selectedConversation.status).color,
                              border: `1px solid ${getStatusInfo(selectedConversation.status).color}40`,
                              fontWeight: 500,
                              flexShrink: 0,
                              ml: 0.5,
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{
                    display: 'flex',
                    gap: isMobileHorizontal ? 0.5 : 1,
                    flexShrink: 0,
                    alignItems: 'center',
                  }}>
                    {/* Mobile vertical: Show only main button + modal */}
                    {isMobile && !isMobileHorizontal ? (
                      <>
                        {/* Main action button */}
                        {selectedConversation.status === 'Finalizado' ? (
                          <Tooltip title="Reabrir Conversa">
                            <IconButton
                              onClick={() => handleReopenConversation(selectedConversation.id)}
                              sx={{
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(245,158,11,0.15)' : '#fffbeb',
                                color: '#f59e0b',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(245,158,11,0.25)' : '#fef3c7',
                                },
                              }}
                              size="small"
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Finalizar Conversa">
                            <IconButton
                              onClick={() => handleFinishConversation(selectedConversation.id)}
                              sx={{
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.15)' : '#f0fdf4',
                                color: '#10b981',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.25)' : '#dcfce7',
                                },
                              }}
                              size="small"
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* More options menu */}
                        <Tooltip title="Mais opções">
                          <IconButton
                            onClick={handleMobileMenuOpen}
                            sx={{
                              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(107,114,128,0.15)' : '#f3f4f6',
                              color: theme.palette.text.secondary,
                              width: 32,
                              height: 32,
                              '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(107,114,128,0.25)' : '#e5e7eb',
                              },
                            }}
                            size="small"
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      /* Desktop and mobile horizontal: Show all buttons */
                      <>
                        {/* Status-specific buttons */}
                        {selectedConversation.status === 'Finalizado' && (
                          <Tooltip title="Reabrir Conversa">
                            <IconButton
                              onClick={() => handleReopenConversation(selectedConversation.id)}
                              sx={{
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(245,158,11,0.15)' : '#fffbeb',
                                color: '#f59e0b',
                                width: isMobileHorizontal ? 28 : 36,
                                height: isMobileHorizontal ? 28 : 36,
                                '&:hover': {
                                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(245,158,11,0.25)' : '#fef3c7',
                                },
                              }}
                              size="small"
                            >
                              <RefreshIcon fontSize={isMobileHorizontal ? "small" : "medium"} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {selectedConversation.status !== 'Finalizado' && (
                          <Tooltip title="Finalizar Conversa">
                            <IconButton
                              onClick={() => handleFinishConversation(selectedConversation.id)}
                              sx={{
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.15)' : '#f0fdf4',
                                color: '#10b981',
                                width: isMobileHorizontal ? 28 : 36,
                                height: isMobileHorizontal ? 28 : 36,
                                '&:hover': {
                                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.25)' : '#dcfce7',
                                },
                              }}
                              size="small"
                            >
                              <CheckCircleIcon fontSize={isMobileHorizontal ? "small" : "medium"} />
                            </IconButton>
                          </Tooltip>
                        )}

                        {relatedLead && (
                          <Tooltip title="Abrir Detalhes do Lead">
                            <IconButton
                              onClick={handleOpenLead}
                              sx={{
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(34,197,94,0.15)' : '#dcfdf4',
                                color: theme.palette.success.dark,
                                width: isMobileHorizontal ? 28 : 36,
                                height: isMobileHorizontal ? 28 : 36,
                                '&:hover': {
                                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(34,197,94,0.25)' : '#bbf7d0',
                                },
                              }}
                              size="small"
                            >
                              <OpenInNewIcon fontSize={isMobileHorizontal ? "small" : "medium"} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!relatedLead && !leadLoading && (
                          <Tooltip title="Criar Lead desta Conversa">
                            <IconButton
                              onClick={handleCreateLeadFromConversation}
                              sx={{
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,152,0,0.15)' : '#fff3e0',
                                color: theme.palette.warning.dark,
                                width: isMobileHorizontal ? 28 : 36,
                                height: isMobileHorizontal ? 28 : 36,
                                '&:hover': {
                                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,152,0,0.25)' : '#ffe0b2',
                                },
                              }}
                              size="small"
                            >
                              <PersonAddIcon fontSize={isMobileHorizontal ? "small" : "medium"} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canAssignConversation() && (
                          <Tooltip title="Atribuir Conversa">
                            <IconButton
                              onClick={() => setAssignModalOpen(true)}
                              sx={{
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(14,165,233,0.15)' : '#e0f2fe',
                                color: theme.palette.info.dark,
                                width: isMobileHorizontal ? 28 : 36,
                                height: isMobileHorizontal ? 28 : 36,
                                '&:hover': {
                                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(14,165,233,0.25)' : '#b3e5fc',
                                },
                              }}
                              size="small"
                            >
                              <SwapHorizIcon fontSize={isMobileHorizontal ? "small" : "medium"} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </Box>
                </Paper>

                {/* Área de mensagens */}
                <Box
                  ref={messagesContainerRef}
                  onScroll={checkIfShouldAutoScroll}
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    p: isMobileHorizontal ? 1.5 : (isMobile ? 2 : 3),
                    backgroundColor: theme.palette.background.default,
                    backgroundImage: theme.palette.mode === 'dark' ? 'none' : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    minHeight: isMobileHorizontal ? '150px' : (isMobile ? '200px' : 'auto'),
                    WebkitOverflowScrolling: 'touch',
                    pb: isMobileHorizontal ? 0.5 : (isMobile ? 1 : 0),
                    scrollBehavior: 'smooth',
                    // Otimizações para mobile horizontal
                    maxHeight: isMobileHorizontal ? 'calc(100vh - 200px)' : 'none',
                  }}
                >
                  {messages.map((message) => {
                    let messageParts;
                    try {
                      messageParts = splitMessageContent(message.content);
                    } catch (error) {
                      console.error('Error processing message content:', error);
                      // Fallback to original content
                      messageParts = [{ text: message.content || '', media: [] }];
                    }

                    return messageParts.map((part, partIndex) => (
                      <Box
                        key={`${message._id.$oid}-${partIndex}`}
                        sx={{
                          display: 'flex',
                          justifyContent: message.role === 'assistant' ? 'flex-end' : 'flex-start',
                          mb: 2,
                        }}
                      >
                        <Box sx={{
                          maxWidth: isMobileHorizontal ? '90%' : (isMobile ? '85%' : '75%'),
                          minWidth: isMobileHorizontal ? '50%' : (isMobile ? '60%' : 'auto')
                        }}>
                          {/* Text content */}
                          {part.text && (
                            <Paper
                              elevation={0}
                              sx={{
                                p: isMobileHorizontal ? 1 : (isMobile ? 1.5 : 2),
                                mb: part.media.length > 0 ? 1 : 0,
                                backgroundColor: message.role === 'assistant' ? theme.palette.info.main : theme.palette.background.paper,
                                color: message.role === 'assistant' ? theme.palette.getContrastText(theme.palette.info.main) : theme.palette.text.primary,
                                borderRadius: message.role === 'assistant'
                                  ? (isMobileHorizontal ? '16px 16px 4px 16px' : (isMobile ? '18px 18px 4px 18px' : '20px 20px 6px 20px'))
                                  : (isMobileHorizontal ? '16px 16px 16px 4px' : (isMobile ? '18px 18px 18px 4px' : '20px 20px 20px 6px')),
                                boxShadow: message.role === 'assistant'
                                  ? (theme.palette.mode === 'dark' ? '0 4px 12px rgba(14,165,233,0.35)' : '0 4px 12px rgba(14, 165, 233, 0.3)')
                                  : (theme.palette.mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0, 0, 0, 0.1)'),
                                border: message.role === 'assistant' ? 'none' : `1px solid ${theme.palette.divider}`,
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                fontSize: isMobileHorizontal ? '0.85rem' : '1rem',
                              }}
                            >
                              <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                                {renderTextWithBold(part.text)}
                              </Typography>
                              {/* Show timestamp on text if no media, or if it's the last part */}
                              {(part.media.length === 0 || partIndex === messageParts.length - 1) && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    textAlign: 'right',
                                    fontSize: '0.75rem',
                                    opacity: 0.8,
                                    color: message.role === 'assistant' ? theme.palette.mode === 'dark' ? '#000' : undefined : theme.palette.text.secondary,
                                  }}
                                >
                                  {formatMessageTime(message.timestamp.$date)}
                                </Typography>
                              )}
                            </Paper>
                          )}

                          {/* Media content */}
                          {part.media.length > 0 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {part.media.map((media, mediaIndex) => (
                                <Box key={`${message._id.$oid}-${partIndex}-media-${mediaIndex}`}>
                                  {media.type === 'image' ? (
                                    <Box
                                      component="img"
                                      src={media.url}
                                      alt={media.alt || 'Image'}
                                      sx={{
                                        maxWidth: '100%',
                                        maxHeight: '300px',
                                        borderRadius: '12px',
                                        objectFit: 'cover',
                                        cursor: 'pointer',
                                        boxShadow: theme.palette.mode === 'dark'
                                          ? '0 2px 8px rgba(0,0,0,0.4)'
                                          : '0 2px 8px rgba(0, 0, 0, 0.1)',
                                        '&:hover': {
                                          opacity: 0.9,
                                        },
                                      }}
                                      onClick={() => window.open(media.url, '_blank')}
                                    />
                                  ) : (
                                    <Box
                                      component="video"
                                      src={media.url}
                                      controls
                                      sx={{
                                        maxWidth: '100%',
                                        maxHeight: '300px',
                                        borderRadius: '12px',
                                        boxShadow: theme.palette.mode === 'dark'
                                          ? '0 2px 8px rgba(0,0,0,0.4)'
                                          : '0 2px 8px rgba(0, 0, 0, 0.1)',
                                      }}
                                    />
                                  )}
                                </Box>
                              ))}
                              {/* Show timestamp on media if no text and it's the last part */}
                              {!part.text && partIndex === messageParts.length - 1 && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    textAlign: message.role === 'assistant' ? 'left' : 'right',
                                    fontSize: '0.75rem',
                                    opacity: 0.8,
                                    color: theme.palette.text.secondary,
                                    mt: 0.5,
                                  }}
                                >
                                  {formatMessageTime(message.timestamp.$date)}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    ));
                  }).flat()}
                  <div ref={messagesEndRef} />
                </Box>

                {/* Preview da imagem selecionada */}
                {imagePreview && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: isMobile ? 1.5 : 2,
                      backgroundColor: theme.palette.background.paper,
                      borderTop: `1px solid ${theme.palette.divider}`,
                      flexShrink: 0, // Prevent preview from shrinking
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Typography variant="body2" color="textSecondary">
                        Imagem selecionada:
                      </Typography>
                      <Button
                        size="small"
                        onClick={handleCancelImage}
                        sx={{ minWidth: 'auto', p: 0.5 }}
                      >
                        <Clear fontSize="small" />
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      <Box
                        component="img"
                        src={imagePreview}
                        alt="Preview"
                        sx={{
                          width: 80,
                          height: 80,
                          objectFit: 'cover',
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder={sendingImage ? "Enviando..." : "Adicionar legenda (opcional)..."}
                          value={imageCaption}
                          onChange={(e) => setImageCaption(e.target.value)}
                          multiline
                          maxRows={3}
                          disabled={sendingImage}
                          sx={{ mb: 1 }}
                        />
                        <Button
                          variant="contained"
                          onClick={handleImageSend}
                          disabled={sendingImage}
                          startIcon={<SendIcon />}
                          size="small"
                          sx={{
                            backgroundColor: theme.palette.primary.main,
                            '&:hover': {
                              backgroundColor: theme.palette.primary.dark,
                            },
                            '&:disabled': {
                              backgroundColor: theme.palette.action.disabledBackground,
                              color: theme.palette.action.disabled,
                            },
                          }}
                        >
                          {sendingImage ? 'Enviando...' : 'Enviar Imagem'}
                        </Button>
                      </Box>
                    </Box>
                  </Paper>
                )}

                {/* Input de mensagem */}
                <Paper
                  elevation={0}
                  sx={{
                    p: isMobileHorizontal ? 1.5 : (isMobile ? 2 : 3),
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobileHorizontal ? 1 : (isMobile ? 1.5 : 2),
                    backgroundColor: theme.palette.background.paper,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    flexShrink: 0,
                    position: (isMobile || isMobileHorizontal) ? 'sticky' : 'static',
                    bottom: 0,
                    zIndex: 1,
                    minHeight: isMobileHorizontal ? '60px' : 'auto',
                  }}
                >
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="image-upload"
                    type="file"
                    disabled={selectedConversation.status === 'Finalizado' || sendingImage || sending || selectedAgent?.whatsAppConnected === 'oficial'}
                    onChange={handleImageSelect}
                  />
                  <label htmlFor="image-upload">
                    <IconButton
                      component="span"
                      disabled={selectedConversation.status === 'Finalizado' || sendingImage || sending || selectedAgent?.whatsAppConnected === 'oficial'}
                      sx={{
                        backgroundColor: (selectedConversation.status === 'Finalizado' || sendingImage || sending || selectedAgent?.whatsAppConnected === 'oficial')
                          ? theme.palette.action.disabledBackground
                          : (theme.palette.mode === 'dark' ? '#1a1a1a' : '#f1f5f9'),
                        '&:hover': {
                          backgroundColor: (selectedConversation.status === 'Finalizado' || sendingImage || sending || selectedAgent?.whatsAppConnected === 'oficial')
                            ? theme.palette.action.disabledBackground
                            : (theme.palette.mode === 'dark' ? '#313131' : '#e2e8f0'),
                        },
                        '&:disabled': {
                          backgroundColor: theme.palette.action.disabledBackground,
                        },
                      }}
                    >
                      <ImageIcon sx={{
                        color: (selectedConversation.status === 'Finalizado' || sendingImage || sending || selectedAgent?.whatsAppConnected === 'oficial')
                          ? theme.palette.action.disabled
                          : '#64748b'
                      }} />
                    </IconButton>
                  </label>


                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder={
                      selectedConversation.status === 'Finalizado'
                        ? 'Conversa finalizada - reabra para enviar mensagens'
                        : sending || sendingImage
                          ? 'Enviando...'
                          : 'Digite sua mensagem...'
                    }
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    multiline
                    maxRows={isMobileHorizontal ? 2 : (isMobile ? 3 : 4)}
                    disabled={selectedConversation.status === 'Finalizado' || sending || sendingImage || selectedAgent?.whatsAppConnected === 'oficial'}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: isMobileHorizontal ? '16px' : (isMobile ? '20px' : '24px'),
                        background: (selectedConversation.status === 'Finalizado' || selectedAgent?.whatsAppConnected === 'oficial')
                          ? (theme.palette.mode === 'dark' ? '#1a1a1a' : '#f3f4f6')
                          : (theme.palette.mode === 'dark' ? '#313131' : '#f8fafc'),
                        border: `2px solid ${theme.palette.divider}`,
                        fontSize: isMobileHorizontal ? '13px' : (isMobile ? '14px' : '16px'),
                        '&:hover': {
                          borderColor: (selectedConversation.status === 'Finalizado' || selectedAgent?.whatsAppConnected === 'oficial')
                            ? theme.palette.divider
                            : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#cbd5e1'),
                        },
                        '&.Mui-focused': {
                          borderColor: "transparent",
                          backgroundColor: (selectedConversation.status === 'Finalizado' || selectedAgent?.whatsAppConnected === 'oficial')
                            ? (theme.palette.mode === 'dark' ? '#1a1a1a' : '#f3f4f6')
                            : theme.palette.background.paper,
                        },
                        '&.Mui-disabled': {
                          backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f3f4f6',
                          color: theme.palette.text.disabled,
                        },
                      },
                    }}
                  />

                  <Tooltip title={
                    selectedConversation.status === 'Finalizado'
                      ? 'Reabra a conversa para usar IA'
                      : (sending || sendingImage)
                        ? 'Aguarde o envio finalizar'
                        : 'Sugerir resposta com IA'
                  }>
                    <IconButton
                      onClick={handleSuggestResponse}
                      disabled={suggestingResponse || messages.length === 0 || !selectedConversation || selectedConversation.status === 'Finalizado' || sending || sendingImage || selectedAgent?.whatsAppConnected === 'oficial'}
                      sx={{
                        backgroundColor: (selectedConversation.status === 'Finalizado' || sending || sendingImage || selectedAgent?.whatsAppConnected === 'oficial')
                          ? theme.palette.action.disabledBackground
                          : (theme.palette.mode === 'dark' ? '#7c3aed' : '#8b5cf6'),
                        color: (selectedConversation.status === 'Finalizado' || sending || sendingImage || selectedAgent?.whatsAppConnected === 'oficial')
                          ? theme.palette.action.disabled
                          : 'white',
                        width: isMobileHorizontal ? 36 : (isMobile ? 40 : 48),
                        height: isMobileHorizontal ? 36 : (isMobile ? 40 : 48),
                        minWidth: isMobileHorizontal ? 36 : (isMobile ? 40 : 48),
                        '&:hover': {
                          backgroundColor: (selectedConversation.status === 'Finalizado' || sending || sendingImage || selectedAgent?.whatsAppConnected === 'oficial')
                            ? theme.palette.action.disabledBackground
                            : (theme.palette.mode === 'dark' ? '#6d28d9' : '#7c3aed'),
                          transform: (selectedConversation.status === 'Finalizado' || sending || sendingImage || selectedAgent?.whatsAppConnected === 'oficial') ? 'none' : 'scale(1.05)',
                        },
                        '&:disabled': {
                          backgroundColor: theme.palette.action.disabledBackground,
                          color: theme.palette.action.disabled,
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      <AutoAwesomeIcon />
                    </IconButton>
                  </Tooltip>

                  <IconButton
                    onClick={handleSendMessage}
                    disabled={!messageText.trim() || sending || selectedConversation.status === 'Finalizado' || selectedAgent?.whatsAppConnected === 'oficial'}
                    sx={{
                      backgroundColor: (selectedConversation.status === 'Finalizado' || selectedAgent?.whatsAppConnected === 'oficial')
                        ? theme.palette.action.disabledBackground
                        : '#25d366',
                      color: (selectedConversation.status === 'Finalizado' || selectedAgent?.whatsAppConnected === 'oficial')
                        ? theme.palette.action.disabled
                        : 'white',
                      width: isMobileHorizontal ? 36 : (isMobile ? 40 : 48),
                      height: isMobileHorizontal ? 36 : (isMobile ? 40 : 48),
                      minWidth: isMobileHorizontal ? 36 : (isMobile ? 40 : 48),
                      '&:hover': {
                        backgroundColor: (selectedConversation.status === 'Finalizado' || selectedAgent?.whatsAppConnected === 'oficial')
                          ? theme.palette.action.disabledBackground
                          : '#20c158',
                        transform: (selectedConversation.status === 'Finalizado' || selectedAgent?.whatsAppConnected === 'oficial') ? 'none' : 'scale(1.05)',
                      },
                      '&:disabled': {
                        backgroundColor: theme.palette.action.disabledBackground,
                        color: theme.palette.action.disabled,
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    <SendIcon />
                  </IconButton>
                </Paper>
              </>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme.palette.background.default,
                  backgroundImage: theme.palette.mode === 'dark' ? 'none' : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                }}
              >
                <Box sx={{ textAlign: 'center', maxWidth: 400, p: 4 }}>
                  <Box
                    sx={{
                      width: 120,
                      height: 120,
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(14,165,233,0.15)' : '#e0f2fe',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 2rem',
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 60, color: theme.palette.info.main }} />
                  </Box>
                  <Typography variant="h4" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}>
                    {!selectedAgent ? 'Selecione um agente' : 'Selecione uma conversa'}
                  </Typography>
                  <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6, mb: !selectedAgent ? 3 : 0 }}>
                    {!selectedAgent
                      ? agentsLoading
                        ? 'Carregando agentes disponíveis...'
                        : availableAgents.length === 0
                          ? 'Nenhum agente habilitado encontrado. Entre em contato com o administrador.'
                          : availableAgents.length === 1
                            ? 'Agente selecionado automaticamente. Aguarde o carregamento das conversas...'
                            : 'Escolha um agente para visualizar suas conversas e começar a responder aos clientes'
                      : 'Escolha uma conversa da lista ao lado para visualizar mensagens e responder ao cliente'
                    }
                  </Typography>
                  {!selectedAgent && availableAgents.length > 1 && !agentsLoading && (
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => setAgentSelectionOpen(true)}
                      sx={{
                        borderRadius: '12px',
                        textTransform: 'none',
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        backgroundColor: theme.palette.info.main,
                        color: theme.palette.mode === 'dark' ? '#000' : undefined,
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark' ? '#0284c7' : '#0284c7',
                        },
                      }}
                    >
                      Selecionar Agente
                    </Button>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Modal de data personalizada */}
      <Dialog
        open={customDateOpen}
        onClose={() => setCustomDateOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
          },
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            Período Personalizado
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              type="date"
              label="Data início"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                },
              }}
            />
            <TextField
              type="date"
              label="Data fim"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={() => setCustomDateOpen(false)}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              px: 3,
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => setCustomDateOpen(false)}
            variant="contained"
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              px: 3,
              backgroundColor: '#0ea5e9',
              '&:hover': {
                backgroundColor: '#0284c7',
              },
            }}
          >
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal do Lead */}
      {relatedLead && (
        <LeadDetailModal
          open={leadDetailOpen}
          onClose={() => setLeadDetailOpen(false)}
          users={[]} // Lista de usuários vazia por enquanto
          agentUsers={agentUsers}
          lead={{
            _id: { $oid: relatedLead.id },
            name: relatedLead.name,
            agentId: relatedLead.agentId,
            phoneNumber: relatedLead.phoneNumber,
            email: relatedLead.email,
            agentPhone: relatedLead.agentPhone || '',
            interested: relatedLead.interested || '',
            resumeAI: relatedLead.resumeAI,
            leadQuality: relatedLead.leadQuality || 'Neutro',
            status: relatedLead.status as any || 'novo',
            assignedTo: relatedLead.assignedTo || '',
            assignedToName: relatedLead.assignedToName || '',
            company: '', // Campo não existe no BackendLead
            observations: '', // Campo não existe no BackendLead
            observacoes: (relatedLead.observacoes || []).map(obs => ({
              _id: obs._id || Math.random().toString(),
              comentario: obs.comentario,
              dataContato: obs.dataContato,
            })),
            campaignInfo: undefined, // Campo não existe no BackendLead
            creationTime: { $date: relatedLead.creationTime || new Date().toISOString() },
          }}
          onUpdateLead={(updatedLead) => {
            // Refresh lead data after update
            if (selectedConversation?.userId) {
              const cleanPhone = selectedConversation.userId.replace(/\D/g, '');
              fetchLeadByPhoneNumber(cleanPhone)
                .then(setRelatedLead)
                .catch(() => setRelatedLead(null));
            }
            setLeadDetailOpen(false);
          }}
          hideConversarButton={true}
        />
      )}

      {/* Modal de Atribuição de Conversa */}
      <AssignConversationModal
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        onAssign={handleAssignConversation}
        conversationName={selectedConversation ? (getConversationLead(selectedConversation)?.name || selectedConversation.name || 'Conversa sem nome') : 'Conversa sem nome'}
        users={agentUsers
          .filter(au => au.isActive && au.agentId === selectedConversation?.agentId)
          .map(au => ({
            id: au.userId,
            name: au.userName || au.userId,
            email: au.userUsername || '',
            role: 'Sales' as const,
            managerId: undefined,
            teamMembers: []
          }))}
      />

      {/* Modal de Seleção de Agente */}
      <AgentSelectionModal
        open={agentSelectionOpen}
        onClose={handleAgentSelectionClose}
        onSelectAgent={handleAgentSelect}
        selectedAgentId={selectedAgent?.id}
        title="Selecionar Agente"
        subtitle="Escolha um agente para visualizar suas conversas"
      />

      {/* Modal de Criação de Lead */}
      <CreateLeadModal
        open={createLeadModalOpen}
        onClose={() => setCreateLeadModalOpen(false)}
        onLeadCreated={handleLeadCreated}
        conversationData={selectedConversation}
        isMobile={isMobile}
      />

      {/* Modal de confirmação de exclusão de conversa */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Limpeza de Histórico</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja limpar o histórico desta conversa?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDeleteConversation} color="error" disabled={deletingConversation}>
            {deletingConversation ? 'Limpando...' : 'Limpar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mobile menu for extra options */}
      <Dialog
        open={Boolean(mobileMenuAnchor)}
        onClose={handleMobileMenuClose}
        PaperProps={{
          sx: {
            position: 'fixed',
            top: 'auto',
            bottom: 0,
            left: 0,
            right: 0,
            margin: 0,
            borderRadius: '16px 16px 0 0',
            maxHeight: '50vh',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, textAlign: 'center' }}>
            Opções da Conversa
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <List>
            {/* Lead-related options */}
            {relatedLead && (
              <ListItemButton
                onClick={() => {
                  handleOpenLead();
                  handleMobileMenuClose();
                }}
                sx={{
                  borderRadius: 1,
                  mx: 1,
                  mb: 0.5,
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ backgroundColor: theme.palette.success.main, width: 32, height: 32 }}>
                    <OpenInNewIcon fontSize="small" />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="Abrir Detalhes do Lead"
                  secondary="Visualizar informações completas"
                />
              </ListItemButton>
            )}
            {!relatedLead && !leadLoading && (
              <ListItemButton
                onClick={() => {
                  handleCreateLeadFromConversation();
                  handleMobileMenuClose();
                }}
                sx={{
                  borderRadius: 1,
                  mx: 1,
                  mb: 0.5,
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ backgroundColor: theme.palette.warning.main, width: 32, height: 32 }}>
                    <PersonAddIcon fontSize="small" />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="Criar Lead desta Conversa"
                  secondary="Gerar novo lead"
                />
              </ListItemButton>
            )}

            {/* Assignment option */}
            {canAssignConversation() && (
              <ListItemButton
                onClick={() => {
                  setAssignModalOpen(true);
                  handleMobileMenuClose();
                }}
                sx={{
                  borderRadius: 1,
                  mx: 1,
                  mb: 0.5,
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ backgroundColor: theme.palette.info.main, width: 32, height: 32 }}>
                    <SwapHorizIcon fontSize="small" />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="Atribuir Conversa"
                  secondary="Transferir para outro usuário"
                />
              </ListItemButton>
            )}
          </List>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleMobileMenuClose}
            fullWidth
            variant="outlined"
            sx={{ borderRadius: '12px' }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default ConversationsPage;